import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import { 
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow 
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    Users, 
    UserPlus,
    TrendingUp, 
    CheckCircle2,
    Clock,
    Send,
    Download,
    Loader2,
    Mail,
    RefreshCw,
    Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { InviteLinkCard } from '../components/InviteLinkCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
    const config = {
        new: { label: 'Neu', className: 'bg-blue-100 text-blue-700' },
        contacted: { label: 'Kontaktiert', className: 'bg-yellow-100 text-yellow-700' },
        qualified: { label: 'Qualifiziert', className: 'bg-purple-100 text-purple-700' },
        converted: { label: 'Konvertiert', className: 'bg-green-100 text-green-700' },
        rejected: { label: 'Abgelehnt', className: 'bg-red-100 text-red-700' },
        pending: { label: 'Ausstehend', className: 'bg-orange-100 text-orange-700' },
        accepted: { label: 'Angenommen', className: 'bg-green-100 text-green-700' }
    };
    const { label, className } = config[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
    return <Badge className={className}>{label}</Badge>;
};

export const SalesRepDashboard = () => {
    const { token } = useAuth();
    const { tenant } = useTenant();
    
    const [stats, setStats] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [referrals, setReferrals] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [formData, setFormData] = useState({ email: '', name: '' });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [statsRes, customersRes, referralsRes, invitationsRes] = await Promise.all([
                axios.get(`${API}/sales/stats`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/sales/customers`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/sales/referrals`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/sales/invitations`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setStats(statsRes.data);
            setCustomers(customersRes.data);
            setReferrals(referralsRes.data);
            setInvitations(invitationsRes.data);
        } catch (error) {
            toast.error('Daten konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.email) {
            toast.error('E-Mail ist erforderlich');
            return;
        }
        
        setSubmitting(true);
        try {
            await axios.post(
                `${API}/sales/invitations`,
                formData,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Einladung an ${formData.email} wurde versendet`);
            setDialogOpen(false);
            setFormData({ email: '', name: '' });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Einladung konnte nicht erstellt werden');
        } finally {
            setSubmitting(false);
        }
    };

    const handleResend = async (invitationId) => {
        try {
            await axios.post(
                `${API}/sales/invitations/${invitationId}/resend`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Einladung erneut gesendet');
        } catch (error) {
            toast.error('Erneutes Senden fehlgeschlagen');
        }
    };

    const handleDeleteInvitation = async (invitationId) => {
        try {
            await axios.delete(`${API}/sales/invitations/${invitationId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Einladung gelöscht');
            fetchData();
        } catch (error) {
            toast.error('Löschen fehlgeschlagen');
        }
    };

    const handleExportCSV = async () => {
        setExporting(true);
        try {
            let url = `${API}/sales/referrals/export`;
            if (selectedIds.size > 0) {
                url += `?ids=${encodeURIComponent(Array.from(selectedIds).join(','))}`;
            }
            
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-16le;' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `empfehlungen_export_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success('Export erfolgreich');
            setSelectedIds(new Set());
        } catch (error) {
            toast.error('Export fehlgeschlagen');
        } finally {
            setExporting(false);
        }
    };

    const toggleSelect = (id) => {
        const newSelected = new Set(selectedIds);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedIds(newSelected);
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === referrals.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(referrals.map(r => r.id)));
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('de-DE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <DashboardLayout>
            <div className="space-y-8 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900">Vertriebler Dashboard</h1>
                        <p className="text-slate-600 mt-1">Verwalten Sie Ihre Kunden und Empfehlungen</p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button 
                                style={{ backgroundColor: tenant?.primary_color }}
                                className="gap-2"
                                data-testid="invite-via-email-btn"
                            >
                                <UserPlus size={18} />
                                Kunde einladen
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Neuen Kunden einladen</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email">E-Mail *</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        placeholder="kunde@beispiel.de"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="name">Name (optional)</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Max Mustermann"
                                    />
                                </div>
                                <div className="flex justify-end gap-3 pt-4">
                                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                        Abbrechen
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        disabled={submitting}
                                        style={{ backgroundColor: tenant?.primary_color }}
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Senden...
                                            </>
                                        ) : (
                                            <>
                                                <Send size={16} className="mr-2" />
                                                Einladung senden
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                                    <Users size={24} className="text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Meine Kunden</p>
                                    <p className="text-2xl font-bold text-slate-900">{stats?.total_customers || 0}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                                    <TrendingUp size={24} className="text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Empfehlungen</p>
                                    <p className="text-2xl font-bold text-slate-900">{stats?.total_referrals || 0}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                                    <CheckCircle2 size={24} className="text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Konvertiert</p>
                                    <p className="text-2xl font-bold text-slate-900">{stats?.converted_referrals || 0}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center">
                                    <Clock size={24} className="text-orange-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Offene Einladungen</p>
                                    <p className="text-2xl font-bold text-slate-900">{stats?.pending_invitations || 0}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Invite Link Card - New System */}
                <InviteLinkCard className="border-2 border-dashed border-blue-200 bg-blue-50/30" />

                {/* Tabs */}
                <Tabs defaultValue="referrals" className="space-y-6">
                    <TabsList>
                        <TabsTrigger value="referrals">Empfehlungen</TabsTrigger>
                        <TabsTrigger value="customers">Kunden</TabsTrigger>
                        <TabsTrigger value="invitations">Einladungen</TabsTrigger>
                    </TabsList>

                    {/* Referrals Tab */}
                    <TabsContent value="referrals">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>Empfehlungen meiner Kunden</CardTitle>
                                <div className="flex items-center gap-3">
                                    {selectedIds.size > 0 && (
                                        <span className="text-sm text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                                            {selectedIds.size} ausgewählt
                                        </span>
                                    )}
                                    <Button
                                        onClick={handleExportCSV}
                                        disabled={exporting || referrals.length === 0}
                                        variant="outline"
                                        className="gap-2"
                                    >
                                        {exporting ? (
                                            <Loader2 size={16} className="animate-spin" />
                                        ) : (
                                            <Download size={16} />
                                        )}
                                        {selectedIds.size > 0 ? `${selectedIds.size} exportieren` : 'Alle exportieren'}
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {loading ? (
                                    <div className="space-y-3">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                                        ))}
                                    </div>
                                ) : referrals.length === 0 ? (
                                    <div className="text-center py-12">
                                        <TrendingUp size={48} className="mx-auto text-slate-300 mb-3" />
                                        <p className="text-slate-500">Noch keine Empfehlungen</p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-12">
                                                    <Checkbox
                                                        checked={selectedIds.size === referrals.length}
                                                        onCheckedChange={toggleSelectAll}
                                                    />
                                                </TableHead>
                                                <TableHead>Empfehlung</TableHead>
                                                <TableHead>Von Kunde</TableHead>
                                                <TableHead>Datum</TableHead>
                                                <TableHead>Status</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {referrals.map((ref) => (
                                                <TableRow key={ref.id} className={selectedIds.has(ref.id) ? "bg-blue-50" : ""}>
                                                    <TableCell>
                                                        <Checkbox
                                                            checked={selectedIds.has(ref.id)}
                                                            onCheckedChange={() => toggleSelect(ref.id)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <div>
                                                            <p className="font-medium">{ref.lead_name}</p>
                                                            <p className="text-sm text-slate-500">{ref.lead_email}</p>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>{ref.user_name}</TableCell>
                                                    <TableCell>{formatDate(ref.created_at)}</TableCell>
                                                    <TableCell><StatusBadge status={ref.status} /></TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Customers Tab */}
                    <TabsContent value="customers">
                        <Card>
                            <CardHeader>
                                <CardTitle>Meine Kunden</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {loading ? (
                                    <div className="space-y-3">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                                        ))}
                                    </div>
                                ) : customers.length === 0 ? (
                                    <div className="text-center py-12">
                                        <Users size={48} className="mx-auto text-slate-300 mb-3" />
                                        <p className="text-slate-500">Noch keine Kunden</p>
                                        <p className="text-sm text-slate-400 mt-1">Laden Sie Kunden ein, um loszulegen</p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Name</TableHead>
                                                <TableHead>E-Mail</TableHead>
                                                <TableHead>Punkte</TableHead>
                                                <TableHead>Registriert</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {customers.map((customer) => (
                                                <TableRow key={customer.id}>
                                                    <TableCell className="font-medium">{customer.name}</TableCell>
                                                    <TableCell>{customer.email}</TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline">{customer.points_balance} Pkt.</Badge>
                                                    </TableCell>
                                                    <TableCell>{formatDate(customer.created_at)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Invitations Tab */}
                    <TabsContent value="invitations">
                        <Card>
                            <CardHeader>
                                <CardTitle>Meine Einladungen</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {loading ? (
                                    <div className="space-y-3">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                                        ))}
                                    </div>
                                ) : invitations.length === 0 ? (
                                    <div className="text-center py-12">
                                        <Mail size={48} className="mx-auto text-slate-300 mb-3" />
                                        <p className="text-slate-500">Noch keine Einladungen</p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>E-Mail</TableHead>
                                                <TableHead>Name</TableHead>
                                                <TableHead>Datum</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead className="text-right">Aktionen</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {invitations.map((inv) => (
                                                <TableRow key={inv.id}>
                                                    <TableCell>{inv.email}</TableCell>
                                                    <TableCell>{inv.name || '-'}</TableCell>
                                                    <TableCell>{formatDate(inv.created_at)}</TableCell>
                                                    <TableCell><StatusBadge status={inv.status} /></TableCell>
                                                    <TableCell className="text-right">
                                                        {inv.status === 'pending' && (
                                                            <div className="flex justify-end gap-2">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleResend(inv.id)}
                                                                >
                                                                    <RefreshCw size={14} />
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="text-red-500 hover:text-red-600"
                                                                    onClick={() => handleDeleteInvitation(inv.id)}
                                                                >
                                                                    <Trash2 size={14} />
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
};
